import { nextTick } from 'vue'
import type { Router } from 'vue-router'
import { isDesktopRuntime, platformInvoke } from '../platform/bridge'

interface UiSnapshot {
  routeName: string
  scrollX: number
  scrollY: number
}
interface RecoveryContext {
  version: number
  nonce: string
  snapshot: UiSnapshot | null
}

// An unavailable/older native protocol must never prevent the ordinary page from booting.
async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  let timer: number | undefined
  try {
    return await Promise.race([
      platformInvoke<T>(command, args),
      new Promise<never>((_, reject) => {
        timer = window.setTimeout(() => reject(new Error('recovery_ipc_timeout')), 2000)
      }),
    ])
  } finally {
    if (timer !== undefined) window.clearTimeout(timer)
  }
}

async function readContext(): Promise<RecoveryContext> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const context = await invoke<RecoveryContext>('webview_recovery_context')
      if (context.version !== 1 || !/^[a-f0-9]{32}$/.test(context.nonce)) {
        throw new Error('recovery_protocol_unavailable')
      }
      return context
    } catch (error) {
      if (attempt >= 2 || !String(error).includes('recovery_observer_pending')) throw error
      await new Promise<void>((resolve) => window.setTimeout(resolve, 100))
    }
  }
}

function safeRoute(router: Router, name: unknown): name is string {
  if (typeof name !== 'string' || !/^[A-Za-z0-9_-]{1,80}$/.test(name)) return false
  const route = router.getRoutes().find((candidate) => candidate.name === name)
  return Boolean(route && !route.path.includes(':') && !route.redirect && !route.path.startsWith('/login'))
}

export async function prepareWebviewRecovery(router?: Router): Promise<{ ready: () => Promise<void> }> {
  const disabled = { ready: async () => {} }
  if (!isDesktopRuntime) return disabled
  let context: RecoveryContext
  try {
    context = await readContext()
  } catch {
    return disabled
  }
  if (router && context.snapshot && safeRoute(router, context.snapshot.routeName)) {
    try {
      // Named static routes only. Existing auth/permission guards still decide navigation.
      await router.replace({ name: context.snapshot.routeName, params: {}, query: {}, hash: '' })
    } catch {
      // Missing permissions or a changed remote route must not block page initialization.
    }
  }

  return {
    ready: async () => {
      await nextTick()
      try {
        await invoke('webview_recovery_ready', { nonce: context.nonce })
      } catch {
        return
      }
      if (!router) return
      if (context.snapshot && router.currentRoute.value.name === context.snapshot.routeName) {
        const { scrollX, scrollY } = context.snapshot
        if (Number.isFinite(scrollX) && Number.isFinite(scrollY) && scrollX >= 0 && scrollY >= 0
          && scrollX <= 10_000_000 && scrollY <= 10_000_000) {
          window.scrollTo(scrollX, scrollY)
        }
      }
      let timer: number | undefined
      let stopped = false
      let inFlight = false
      const checkpoint = async () => {
        const route = router.currentRoute.value
        if (stopped || inFlight || !safeRoute(router, route.name)
          || Object.keys(route.params).length > 0 || Object.keys(route.query).length > 0 || route.hash) return
        inFlight = true
        try {
          await invoke('webview_recovery_checkpoint', {
            nonce: context.nonce,
            snapshot: {
              routeName: router.currentRoute.value.name,
              scrollX: Math.min(10_000_000, Math.max(0, Math.round(window.scrollX))),
              scrollY: Math.min(10_000_000, Math.max(0, Math.round(window.scrollY))),
            },
          })
        } catch (error) {
          if (String(error).includes('recovery_context_changed')) {
            // Account/profile changes discard old snapshots. Do not replay a snapshot here.
            try {
              context = await readContext()
              await invoke('webview_recovery_ready', { nonce: context.nonce })
            } catch { stopped = true }
          } else { stopped = true }
        } finally {
          inFlight = false
        }
      }
      const schedule = () => {
        if (stopped) return
        if (timer !== undefined) window.clearTimeout(timer)
        timer = window.setTimeout(() => { void checkpoint() }, 500)
      }
      const stopRouter = router.afterEach(schedule)
      window.addEventListener('scroll', schedule, { passive: true })
      window.addEventListener('pagehide', () => {
        stopped = true
        if (timer !== undefined) window.clearTimeout(timer)
        stopRouter()
        window.removeEventListener('scroll', schedule)
      }, { once: true })
      schedule()
    },
  }
}